import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import DashboardLayout from '../Layout/DashboardLayout'
import axios from 'axios'
import { BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import { useAuth } from '../AuthContext'
import Loading from '../element/Loading'
import { Eye, PlusCircle, SquarePen, Calendar, Search, ChevronLeft, ChevronRight } from 'lucide-react'

export default function ClientsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState(null)
  const [details, setDetails] = useState(null)
  const [loadingClients, setLoadingClients] = useState(true)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [paymentInvoiceId, setPaymentInvoiceId] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentNote, setPaymentNote] = useState('')
  const [savingPayment, setSavingPayment] = useState(false)
  const [bulkAmount, setBulkAmount] = useState('')
  const [bulkNote, setBulkNote] = useState('')
  const [bulkSaving, setBulkSaving] = useState(false)
  const [bulkResult, setBulkResult] = useState(null)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [totalClients, setTotalClients] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [stats, setStats] = useState(null)
  const token = Cookies.get('WEB_SSR')

  const selectedClientName = searchParams.get('client_name') || ''
  const selectedClientPhone = searchParams.get('client_phone') || ''

  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true)
      try {
        const params = new URLSearchParams()
        params.append('page', currentPage)
        params.append('per_page', perPage)
        if (search) params.append('search', search)

        const response = await axios.get(`${BASE_URL}/api/clients?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        })

        if (response.data.success) {
          setClients(response.data.data.data || [])
          setCurrentPage(response.data.data.current_page)
          setLastPage(response.data.data.last_page)
          setPerPage(response.data.data.per_page)
          setTotalClients(response.data.data.total)
          setStats(response.data.data.stats || null)
        }
      } catch (error) {
        console.error(error)
        toast.error(t('clients.error.loading'))
      } finally {
        setLoadingClients(false)
      }
    }

    fetchClients()
  }, [token, t, search, currentPage, perPage])

  useEffect(() => {
    if (!selectedClientName) {
      setDetails(null)
      setSelectedClient(null)
      return
    }

    const client = {
      client_name: selectedClientName,
      client_phone: selectedClientPhone,
    }
    setSelectedClient(client)
    fetchClientDetails(client)
  }, [selectedClientName, selectedClientPhone])

  const fetchClientDetails = async (client) => {
    setLoadingDetails(true)
    try {
      const params = new URLSearchParams()
      params.append('client_name', client.client_name)
      params.append('client_phone', client.client_phone || '')

      const response = await axios.get(`${BASE_URL}/api/clients/details?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })

      if (response.data.success) {
        setDetails(response.data.data)
      }
    } catch (error) {
      console.error(error)
      toast.error(t('clients.error.loading_details'))
    } finally {
      setLoadingDetails(false)
    }
  }

  const handleViewClient = (client) => {
    setSearchParams({ client_name: client.client_name, client_phone: client.client_phone || '' })
  }

  const beginPayment = (invoiceId, amount) => {
    setPaymentInvoiceId(invoiceId)
    setPaymentAmount(amount.toFixed(2))
  }

  const handlePaymentSubmit = async () => {
    if (!paymentInvoiceId || !paymentAmount || Number(paymentAmount) <= 0) {
      toast.error(t('clients.error.payment_amount'))
      return
    }

    setSavingPayment(true)
    try {
      const response = await axios.post(
        `${BASE_URL}/api/invoices/${paymentInvoiceId}/payments`,
        {
          amount: Number(paymentAmount),
          note: paymentNote,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        },
      )

      if (response.data.success) {
        toast.success(t('clients.payment.success'))
        if (selectedClient) {
          fetchClientDetails(selectedClient)
        }
        setPaymentInvoiceId('')
        setPaymentAmount('')
        setPaymentNote('')
      } else {
        toast.error(response.data.message || t('clients.error.payment'))
      }
    } catch (error) {
      console.error(error)
      const serverMessage = error.response?.data?.message
      toast.error(serverMessage || t('clients.error.payment'))
    } finally {
      setSavingPayment(false)
    }
  }

  const handleBulkPaymentSubmit = async () => {
    if (!bulkAmount || Number(bulkAmount) <= 0) {
      toast.error(t('clients.error.payment_amount'))
      return
    }

    setBulkSaving(true)
    setBulkResult(null)
    try {
      const response = await axios.post(
        `${BASE_URL}/api/clients/pay-distribute`,
        {
          client_name: details.client_name,
          client_phone: details.client_phone || '',
          amount: Number(bulkAmount),
          note: bulkNote,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        },
      )

      if (response.data.success) {
        toast.success(t('clients.bulk_payment_success'))
        setBulkResult(response.data.data)
        setBulkAmount('')
        setBulkNote('')
        if (selectedClient) {
          fetchClientDetails(selectedClient)
        }
      } else {
        toast.error(response.data.message || t('clients.error.payment'))
      }
    } catch (error) {
      console.error(error)
      toast.error(error.response?.data?.message || t('clients.error.payment'))
    } finally {
      setBulkSaving(false)
    }
  }

  const getPaginationButtons = () => {
    const buttons = []
    if (lastPage <= 7) {
      for (let i = 1; i <= lastPage; i++) {
        buttons.push(i)
      }
    } else {
      buttons.push(1)
      if (currentPage > 3) buttons.push('...')
      let start = Math.max(2, currentPage - 1)
      let end = Math.min(lastPage - 1, currentPage + 1)
      if (currentPage <= 3) { start = 2; end = 4 }
      if (currentPage >= lastPage - 2) { start = lastPage - 3; end = lastPage - 1 }
      for (let i = start; i <= end; i++) buttons.push(i)
      if (currentPage < lastPage - 2) buttons.push('...')
      buttons.push(lastPage)
    }
    return buttons
  }

  const goToPage = (page) => {
    if (page === '...') return
    setCurrentPage(page)
  }

  const startItem = (currentPage - 1) * perPage + 1
  const endItem = Math.min(currentPage * perPage, totalClients)

  const transactionTimeline = useMemo(() => {
    if (!details) return []

    const transactions = []

    // Add invoices
    if (details.invoices && Array.isArray(details.invoices)) {
      details.invoices.forEach((invoice) => {
        transactions.push({
          type: 'invoice',
          id: `inv-${invoice.id}`,
          date: new Date(invoice.created_at),
          invoice_code: invoice.invoice_code,
          total: invoice.total,
          status: invoice.status,
        })
      })
    }

    // Add payments
    if (details.payments && Array.isArray(details.payments)) {
      details.payments.forEach((payment) => {
        transactions.push({
          type: 'payment',
          id: `pay-${payment.id}`,
          date: new Date(payment.payment_date),
          amount: payment.amount,
          note: payment.note,
          invoice_id: payment.invoice_id,
        })
      })
    }

    // Sort by date descending (most recent first)
    return transactions.sort((a, b) => b.date - a.date)
  }, [details])

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl max-lg:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">{t('clients.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('clients.subtitle')}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900 dark:bg-gray-800 dark:text-gray-100">
              {t('clients.total_clients')}: {totalClients}
            </div>
          </div>
        </div>

        {stats && (
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.total_invoiced')}</div>
              <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-50">{Number(stats.total_invoiced).toFixed(2)}</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.total_paid')}</div>
              <div className="mt-1 text-xl font-bold text-green-600 dark:text-green-400">{Number(stats.total_paid).toFixed(2)}</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.unpaid_amount')}</div>
              <div className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">{Number(stats.total_unpaid).toFixed(2)}</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.total_clients')}</div>
              <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-50">{stats.total_clients}</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.unpaid_invoices')}</div>
              <div className="mt-1 text-xl font-bold text-orange-600 dark:text-orange-400">{stats.unpaid_invoices_count}</div>
            </div>
          </div>
        )}

        <div className=" ">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('clients.client_list')}</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('clients.search_placeholder')}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setCurrentPage(1) }}
                    className="w-64 rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm text-gray-900 dark:text-gray-100">
                  <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3">{t('clients.name')}</th>
                      <th className="px-4 py-3">{t('clients.phone')}</th>
                      <th className="px-4 py-3">{t('clients.invoices')}</th>
                      <th className="px-4 py-3">{t('clients.total_invoiced')}</th>
                      <th className="px-4 py-3">{t('clients.balance')}</th>
                      <th className="px-4 py-3">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingClients ? (
                      <tr>
                        <td colSpan="100" className="text-center py-4">
                            <div className="flex justify-center">
                              <Loading size={10} />
                            </div>
                          </td>
                      </tr>
                    ) : clients.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">{t('clients.no_clients')}</td>
                      </tr>
                    ) : (
                      clients.map((client) => (
                        <tr key={`${client.client_name}-${client.client_phone || 'none'}`} className="border-b border-gray-200 dark:border-gray-700">
                          <td className="px-4 py-3 align-top">{client.client_name}</td>
                          <td className="px-4 py-3 align-top">{client.client_phone || '—'}</td>
                          <td className="px-4 py-3 align-top">{client.invoice_count}</td>
                          <td className="px-4 py-3 align-top">{Number(client.total_invoiced).toFixed(2)}</td>
                          <td className="px-4 py-3 align-top">{Number(client.balance).toFixed(2)}</td>
                          <td className="px-4 py-3 align-top text-right">
                            {can('clients.view') && (
                              <button
                                type="button"
                                onClick={() => handleViewClient(client)}
                                className="inline-flex items-center gap-2 rounded-lg bg-[#171717] px-3 py-2 text-xs font-semibold text-white transition hover:bg-gray-800"
                              >
                                <Eye className="w-4 h-4" />
                                {t('clients.view_history')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {clients.length > 0 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {t('products.pagination.showing')} {startItem} {t('products.pagination.to')} {endItem} {t('products.pagination.of')} {totalClients} {t('clients.title')}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-2.5 py-2 border border-gray-200 cursor-pointer dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <ChevronLeft className="w-3 h-3" />
                    </button>
                    <div className="flex items-center gap-1 mx-1">
                      {getPaginationButtons().map((btn, index) => (
                        <button
                          key={index}
                          onClick={() => goToPage(btn)}
                          disabled={btn === '...'}
                          className={`
                            min-w-[32px] h-8 px-2 rounded-lg text-xs font-medium transition-colors
                            ${btn === currentPage
                              ? 'bg-[#171717] text-white cursor-default'
                              : btn === '...'
                                ? 'cursor-default text-gray-500 dark:text-gray-400 hover:bg-transparent'
                                : 'border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                            }
                          `}
                        >
                          {btn}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage >= lastPage}
                      className="px-2.5 py-2 border border-gray-200 cursor-pointer dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {details && (
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('clients.client_history_title')}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('clients.client_history_description')}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                    {details.client_name} {details.client_phone ? `(${details.client_phone})` : ''}
                  </div>
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.invoices')}</div>
                    <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-50">{details.invoice_count}</div>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.total_invoiced')}</div>
                    <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-50">{Number(details.total_invoiced).toFixed(2)}</div>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.total_paid')}</div>
                    <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-50">{Number(details.total_paid).toFixed(2)}</div>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('clients.unpaid_amount')}</div>
                    <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-50">{Number(details.unpaid_amount).toFixed(2)}</div>
                  </div>
                </div>

                <div className="mt-6 grid gap-4 xl:grid-cols-[2fr_1fr]">
                  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('clients.invoices')}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('clients.invoice_list_description')}</p>
                      </div>
                    </div>
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full text-left text-sm text-gray-900 dark:text-gray-100">
                        <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                          <tr>
                            <th className="px-4 py-3">{t('clients.invoice_code')}</th>
                            <th className="px-4 py-3">{t('clients.status')}</th>
                            <th className="px-4 py-3">{t('invoices.remise')}</th>
                            <th className="px-4 py-3">{t('clients.total')}</th>
                            <th className="px-4 py-3">{t('clients.paid')}</th>
                            <th className="px-4 py-3">{t('clients.balance')}</th>
                            <th className="px-4 py-3">&nbsp;</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.invoices.map((invoice) => (
                            <tr key={invoice.id} className="border-b border-gray-200 dark:border-gray-700">
                              <td className="px-4 py-3 align-top">{invoice.invoice_code}</td>
                              <td className="px-4 py-3 align-top">{t(`invoices.status.${invoice.status}`)}</td>
                              <td className="px-4 py-3 align-top">{invoice.remise > 0 ? `-${Number(invoice.remise).toFixed(2)}` : '—'}</td>
                              <td className="px-4 py-3 align-top">{Number(invoice.total).toFixed(2)}</td>
                              <td className="px-4 py-3 align-top">{Number(invoice.paid).toFixed(2)}</td>
                              <td className="px-4 py-3 align-top">{Number(invoice.balance).toFixed(2)}</td>
                              <td className="px-4 py-3 align-top text-right">
                                {can('invoices.pay') && (
                                  <button
                                    type="button"
                                    onClick={() => beginPayment(invoice.id, invoice.balance)}
                                    disabled={invoice.balance <= 0}
                                    className="inline-flex items-center gap-2 rounded-lg bg-[#171717] px-3 py-2 text-xs font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <PlusCircle className="w-4 h-4" />
                                    {t('clients.add_payment')}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 max-h-max">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('clients.payment_form_title')}</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('clients.payment_form_description')}</p>
                    <div className="mt-4 space-y-4">
                      <label className="block text-sm text-gray-700 dark:text-gray-300">
                        <span>{t('clients.payment_invoice')}</span>
                        <select
                          value={paymentInvoiceId}
                          onChange={(event) => setPaymentInvoiceId(event.target.value)}
                          className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        >
                          <option value="">{t('clients.select_invoice')}</option>
                          {details.invoices.map((invoice) => (
                            <option key={invoice.id} value={invoice.id}>
                              {invoice.invoice_code} — {Number(invoice.balance).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-sm text-gray-700 dark:text-gray-300">
                        <span>{t('clients.payment_amount')}</span>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={paymentAmount}
                          onChange={(event) => setPaymentAmount(event.target.value)}
                          className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                          placeholder="0.00"
                        />
                      </label>
                      <label className="block text-sm text-gray-700 dark:text-gray-300">
                        <span>{t('clients.payment_note')}</span>
                        <input
                          value={paymentNote}
                          onChange={(event) => setPaymentNote(event.target.value)}
                          className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                          placeholder={t('clients.payment_note_placeholder')}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={handlePaymentSubmit}
                        disabled={savingPayment}
                        className="inline-flex items-center justify-center rounded-lg bg-[#171717] px-4 py-3 text-xs font-semibold text-white transition hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {savingPayment ? 
                        <div className="flex items-center gap-1">
                            <Loading size={5} className="mr-2" />
                            {t('clients.payment_saving') }
                          </div>
                        : 
                        t('clients.payment_submit')}
                      </button>
                    </div>
                  </div>
                </div>

                {can('invoices.pay') && (
                <div className="rounded-2xl border border-gray-200 bg-white mt-5 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('clients.bulk_payment_title')}</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('clients.bulk_payment_description')}</p>

                  {details.invoices.some(inv => inv.balance > 0) ? (
                    <div className="mt-4 space-y-4">
                      <label className="block text-sm text-gray-700 dark:text-gray-300">
                        <span>{t('clients.bulk_payment_amount')}</span>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={bulkAmount}
                          onChange={(e) => setBulkAmount(e.target.value)}
                          className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                          placeholder="0.00"
                        />
                      </label>
                      <label className="block text-sm text-gray-700 dark:text-gray-300">
                        <span>{t('clients.payment_note')}</span>
                        <input
                          value={bulkNote}
                          onChange={(e) => setBulkNote(e.target.value)}
                          className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                          placeholder={t('clients.payment_note_placeholder')}
                        />
                      </label>

                      <div>
                        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('clients.bulk_payment_preview')}</div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {(() => {
                            const remaining = Number(bulkAmount) || 0
                            let rem = remaining
                            const unpaid = details.invoices
                              .filter(inv => inv.balance > 0)
                              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                              .map(inv => {
                                const alloc = Math.min(rem, inv.balance)
                                rem -= alloc
                                return { ...inv, allocation: alloc }
                              })
                            return unpaid.length > 0 ? (
                              unpaid.map(inv => (
                                <div key={inv.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800">
                                  <span className="text-gray-900 dark:text-gray-50">{inv.invoice_code}</span>
                                  <span className={`font-semibold ${inv.allocation > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                                    {Number(inv.allocation).toFixed(2)}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <p className="text-xs text-gray-500 dark:text-gray-400">{t('clients.bulk_payment_no_unpaid')}</p>
                            )
                          })()}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleBulkPaymentSubmit}
                        disabled={bulkSaving || !bulkAmount || Number(bulkAmount) <= 0}
                        className="inline-flex items-center justify-center rounded-lg bg-[#171717] px-4 py-3 text-xs font-semibold text-white transition hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {
                          bulkSaving ? 
                          <div className="flex items-center gap-1">
                            <Loading size={5} className="mr-2" />
                            {t('clients.bulk_payment_saving') }
                          </div>

                          : 
                            t('clients.bulk_payment_submit')}
                      </button>

                      {bulkResult && (
                        <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-200">
                          {t('clients.bulk_payment_success')} — {Number(bulkResult.distributed).toFixed(2)} {t('clients.total_invoiced')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('clients.bulk_payment_no_unpaid')}</p>
                  )}
                </div>
                )}

                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('clients.payments')}</h3>
                  {details.payments.length > 0 ? (
                    <div className="mt-4 space-y-3">
                      {details.payments.map((payment) => (
                        <div key={payment.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                          <div className="flex items-center justify-between gap-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('clients.payment_amount')}: {Number(payment.amount).toFixed(2)}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{new Date(payment.payment_date).toLocaleDateString()}</div>
                          </div>
                          {payment.note && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{payment.note}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('clients.no_payments')}</p>
                  )}
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">{t('clients.transaction_history')}</h3>
                  {transactionTimeline.length > 0 ? (
                    <div className="mt-4 space-y-3">
                      {transactionTimeline.map((transaction) => (
                        <div
                          key={transaction.id}
                          className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              {transaction.type === 'invoice' ? (
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                                      {t('clients.invoice')}
                                    </span>
                                    <span className="font-semibold text-gray-900 dark:text-gray-50">
                                      {transaction.invoice_code}
                                    </span>
                                  </div>
                                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                    {t('invoices.total')}: {Number(transaction.total).toFixed(2)}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {transaction.status === 'regle' ? (
                                      <span className="text-green-600 dark:text-green-400">{t('invoices.status.regle')}</span>
                                    ) : (
                                      <span className="text-yellow-600 dark:text-yellow-400">{t('invoices.status.not_yet')}</span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800 dark:bg-green-900 dark:text-green-100">
                                      {t('clients.payment')}
                                    </span>
                                    <span className="font-semibold text-gray-900 dark:text-gray-50">
                                      {Number(transaction.amount).toFixed(2)}
                                    </span>
                                  </div>
                                  {transaction.note && (
                                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                      {transaction.note}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              <Calendar className="w-4 h-4" />
                              {transaction.date.toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('clients.no_transactions')}</p>
                  )}
                </div>
              </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
